import React, { memo } from 'react';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { Flex } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';

export const CREATE_WEDDING_CONTAINER_MAX_WIDTH = 346 as const;

const PRINT_IMAGE = 'https://withjoy.com/assets/public/createwedding/print-v2.png?resize=w600' as const;
const PRINT_IMAGE_2X = 'https://withjoy.com/assets/public/createwedding/print-v2.png?resize=w1200' as const;
const GUESTS_IMAGE = 'https://withjoy.com/assets/public/createwedding/guests-v2.png?resize=w600' as const;
const GUESTS_IMAGE_2X = 'https://withjoy.com/assets/public/createwedding/guests-v2.png?resize=w1200' as const;
const WEBSITE_IMAGE = 'https://withjoy.com/assets/public/createwedding/website-v3.png?resize=h800' as const;
const WEBSITE_IMAGE_2X = 'https://withjoy.com/assets/public/createwedding/website-v3.png?resize=h1600' as const;
const MOBILE_WEBSITE_IMAGE = 'https://withjoy.com/assets/public/createwedding/website-mobile-v2.png?resize=w600' as const;
const MOBILE_WEBSITE_IMAGE_2X = 'https://withjoy.com/assets/public/createwedding/website-mobile-v2.png?resize=w1200' as const;

export const WeddingFormTopBar: React.FC = () => {
  const { tq } = useTranslation('createWedding');

  return (
    <Flex
      width="100%"
      backgroundColor="#4C597F"
      alignItems="flex-start"
      justifyContent="flex-start"
      overflow="hidden"
    >
      <Flex
        as="img"
        objectFit="cover"
        objectPosition="center"
        width="100%"
        height="100%"
        src={MOBILE_WEBSITE_IMAGE}
        srcSet={`${MOBILE_WEBSITE_IMAGE} 1x, ${MOBILE_WEBSITE_IMAGE_2X} 2x`}
        alt={tq`Website`}
        loading="lazy"
      />
    </Flex>
  );
};

interface CreateWeddingLayoutProps
  extends Readonly<{
    children: React.ReactNode;
    topPanel?: React.ReactNode;
  }> {}

/**
 * This component is the layout for the Create Wedding on mobile.
 * It renders the form and the images on the left side of the screen.
 */
const CreateWeddingLayoutMobile = ({ children, topPanel }: CreateWeddingLayoutProps): React.ReactElement => {
  const hasTopPanel = Boolean(topPanel);

  return (
    <Flex
      alignItems={'center'}
      flexDirection={{ _: 'column', md: 'row' }}
    >
      {topPanel}
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        position={'relative'}
        paddingX={6}
        minHeight={hasTopPanel ? 'auto' : '100vh'}
        paddingTop={pxToRem(hasTopPanel ? 40 : 0)}
        paddingBottom={hasTopPanel ? 7 : 0}
      >
        {children}
      </Flex>
    </Flex>
  );
};

// Memoized to prevent re-renders when parent re-renders due to view changes
const DesktopAssetsPanel: React.FC = memo(() => {
  const { tq } = useTranslation('createWedding');

  return (
    <Flex
      width={'100%'}
      height={'100%'}
      padding={7}
      flexDirection={'column'}
      gap={6}
    >
      <Flex
        gap={6}
        flex={1}
        minHeight={0}
      >
        <Flex
          flex={1}
          overflow={'hidden'}
          borderRadius={pxToRem(8)}
          display="grid"
        >
          <Flex
            as="img"
            objectFit="cover"
            objectPosition="center"
            width="100%"
            height="100%"
            src={GUESTS_IMAGE}
            srcSet={`${GUESTS_IMAGE} 1x, ${GUESTS_IMAGE_2X} 2x`}
            alt={tq`Guests`}
            loading="lazy"
          />
        </Flex>
        <Flex
          flex={1}
          overflow={'hidden'}
          borderRadius={pxToRem(8)}
          display="grid"
        >
          <Flex
            as="img"
            objectFit="cover"
            objectPosition="center"
            width="100%"
            height="100%"
            src={PRINT_IMAGE}
            srcSet={`${PRINT_IMAGE} 1x, ${PRINT_IMAGE_2X} 2x`}
            alt={tq`Print`}
            loading="lazy"
          />
        </Flex>
      </Flex>
      <Flex
        flex={1}
        minHeight={0}
        overflow={'hidden'}
        borderRadius={pxToRem(8)}
        display="grid"
      >
        <Flex
          as="img"
          objectFit="cover"
          objectPosition="center"
          width="100%"
          height="100%"
          src={WEBSITE_IMAGE}
          srcSet={`${WEBSITE_IMAGE} 1x, ${WEBSITE_IMAGE_2X} 2x`}
          alt={tq`Website`}
          loading="lazy"
        />
      </Flex>
    </Flex>
  );
});

DesktopAssetsPanel.displayName = 'DesktopAssetsPanel';

/**
 * This component is the layout for the Create Wedding on desktop.
 * It renders the form and the images on the left side of the screen.
 */
const CreateWeddingLayoutDesktop = ({ children, topPanel }: CreateWeddingLayoutProps): React.ReactElement => {
  return (
    <Flex alignItems={'center'}>
      {topPanel}
      <Flex
        width={'54%'}
        height={'100vh'}
        backgroundColor={'white'}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <DesktopAssetsPanel />
      </Flex>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width={'46%'}
        minHeight="100vh"
        position={'relative'}
        paddingX={9}
      >
        {children}
      </Flex>
    </Flex>
  );
};

/**
 * Shared layout wrapper for all CreateWedding routes.
 * This component persists across route changes to prevent image re-rendering.
 * The layout (including images) stays mounted while only the content changes.
 */
export const CreateWeddingLayout: React.FC<CreateWeddingLayoutProps> = ({ children, topPanel }) => {
  const isMobileOrTablet = useIsMobileOrTablet();

  if (isMobileOrTablet) {
    return <CreateWeddingLayoutMobile topPanel={topPanel}>{children}</CreateWeddingLayoutMobile>;
  }

  return <CreateWeddingLayoutDesktop topPanel={topPanel}>{children}</CreateWeddingLayoutDesktop>;
};
